import React, { useRef, useState, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  Grid,
  CircularProgress,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DeleteIcon from "@mui/icons-material/Delete";
import { apiData } from "../../services/api_service";
import { uploadToVercelAPI, deleteFromVercelAPI } from "../../config";

const DropZone = styled(Box)(({ theme }) => ({
  width: "95%",
  height: 260,
  border: "2px dashed #d3dce6",
  backgroundColor: "#f9fbfd",
  borderRadius: 12,
  padding: theme.spacing(2),
  textAlign: "center",
  cursor: "pointer",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  "&:hover": {
    borderColor: theme.palette.primary.main,
  },
}));

const HiddenInput = styled("input")({
  display: "none",
});

const ImagePreview = styled("img")({
  width: "100%",
  height: "180px",
  objectFit: "cover",
  borderRadius: "12px",
  border: "1px solid #ccc",
});

const UploadBox = ({ images = [], onFilesChange, maxFiles }) => {
  const inputRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [loadingIndex, setLoadingIndex] = useState(null);
  const MAX_FILE_SIZE_MB = 2;

  useEffect(() => {
    if (images && images.length > 0) {
      setFiles(images.filter((img) => img && img.trim && img.trim() !== ""));
    } else {
      setFiles([]);
    }
  }, [images]);

  const handleBrowseClick = () => inputRef.current?.click();

  const handleRemove = async (index) => {
    try {
      const fileUrl = files[index];
      await apiData("post", deleteFromVercelAPI, { fileUrl });
      const newFiles = files.filter((_, i) => i !== index);
      setFiles(newFiles);
      onFilesChange?.(newFiles);
    } catch (err) {
      console.error("Failed to delete file:", err);
      alert("Failed to delete file.");
    }
  };

  const handleFiles = async (selectedFiles) => {
    try {
      const validFiles = selectedFiles.filter(
        (file) => file.size <= MAX_FILE_SIZE_MB * 1024 * 1024
      );
      if (validFiles.length < selectedFiles.length) {
        alert(`Some files exceed the ${MAX_FILE_SIZE_MB} MB limit.`);
      }

      const filesToUpload = validFiles;

      const uploadedUrls = [];

      for (let i = 0; i < filesToUpload.length; i++) {
        try {
          setLoadingIndex(i);
          const formData = new FormData();
          formData.append("file", filesToUpload[i]);
          const response = await apiData("post", uploadToVercelAPI, formData);
          if (response?.success && response.data) {
            uploadedUrls.push(response.data); // this is the blob URL
          }
        } catch (err) {
          console.error("Upload failed for file:", filesToUpload[i].name, err);
          alert(`Upload failed for ${filesToUpload[i].name}`);
        } finally {
          setLoadingIndex(null);
        }
      }

      const updatedFiles = [...files, ...uploadedUrls];
      setFiles(updatedFiles);
      onFilesChange?.(updatedFiles);
    } catch (err) {
      console.error("Error during file upload:", err);
      alert("An error occurred during file upload.");
    }
  };

  const handleFileChange = (event) =>
    handleFiles(Array.from(event.target.files || []));
  const handleDrop = (event) => {
    event.preventDefault();
    handleFiles(Array.from(event.dataTransfer.files || []));
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Typography variant="h6" mb={2}>
        Image Upload{" "}
        <span style={{ color: "red", fontSize: "0.9rem" }}>
          (Maximum file size per image: 2 MB)
        </span>
      </Typography>

      <DropZone
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
        onClick={handleBrowseClick}
      >
        {loadingIndex !== null && files.length === 0 ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
          >
            <CircularProgress size={40} />
            <Typography variant="body2" sx={{ mt: 1, color: "text.secondary" }}>
              Please wait, uploading your file...
            </Typography>
          </Box>
        ) : (
          <>
            <UploadFileIcon fontSize="large" sx={{ color: "#9fa8b3", mb: 1 }} />
            <Typography variant="body2" color="textSecondary">
              Browse / Drop images here
            </Typography>
          </>
        )}
        <HiddenInput
          type="file"
          ref={inputRef}
          multiple
          accept="image/*"
          onChange={handleFileChange}
        />
      </DropZone>

      {files.length > 0 && (
        <Grid container spacing={2} mt={2}>
          {files.map((url, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Box position="relative">
                <ImagePreview src={url} alt={`Uploaded ${index}`} />
                {loadingIndex === index && (
                  <Box
                    position="absolute"
                    top={0}
                    left={0}
                    right={0}
                    bottom={0}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    bgcolor="rgba(255,255,255,0.6)"
                  >
                    <CircularProgress size={30} />
                  </Box>
                )}
                <IconButton
                  size="small"
                  onClick={() => handleRemove(index)}
                  sx={{
                    position: "absolute",
                    top: 4,
                    right: 4,
                    backgroundColor: "#fff",
                    boxShadow: 1,
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default UploadBox;
